<template>
  <div
    class="admin-user-invitation-detail bg-white-custom border px-3 py-3"
  >
    <b-overlay :show="isLoading" rounded="sm">
      <div
        v-if="inviteId && currentInvitation && currentInvitation.invite_id"
        class="admin-user-invitation-detail--header d-flex align-items-center justify-content-between"
      >
        <h2 class="mb-0">
          {{ `${currentInvitation.first_name} ${currentInvitation.last_name}` }}
        </h2>
        <div class="d-flex align-items-center flex-nowrap">
          <span
            v-for="role in currentInvitation.roles"
            :key="`${currentInvitation.invite_id}_${role}`"
            :class="`${role} badge float-right font-weight-medium mr-2 text-capitalize`"
            >{{ role }}</span
          >
        </div>
      </div>
      <hr v-if="inviteId && currentInvitation && currentInvitation.invite_id" />
      <div class="admin-user-invitation-detail--body">
        <b-row>
          <b-col cols="12" md="6">
            <b-form-group
              id="fieldset-1"
              label="First name:"
              style="font-weight: 500"
              label-for="input-fname"
            >
              <b-form-input
                id="input-fname"
                v-model="currentInvitation['first_name']"
                trim
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="6">
            <b-form-group
              id="fieldset-2"
              label="Last name:"
              style="font-weight: 500"
              label-for="input-lname"
            >
              <b-form-input
                id="input-lname"
                v-model="currentInvitation['last_name']"
                trim
                disabled
              />
            </b-form-group>
          </b-col>
          <b-col cols="12">
            <b-form-group
              id="fieldset-3"
              label="Email Address:"
              label-for="input-email"
              style="font-weight: 500"
            >
              <b-form-input
                id="input-email"
                v-model="currentInvitation['email']"
                disabled
                trim
              ></b-form-input>
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <hr v-if="inviteId && currentInvitation && currentInvitation.invite_id" />
      <div
        v-if="inviteId && currentInvitation && currentInvitation.invite_id"
        class="admin-user-invitation-detail--footer d-flex align-items-center justify-content-end"
      >
        <b-button
          v-if="!currentInvitation.fulfilled_at"
          variant="success"
          pill
          size="sm"
          :disabled="resending || revoking"
          @click="resendInvitation"
        >
          <b-spinner v-if="resending" small></b-spinner>
          <span v-else>Resend</span> </b-button
        ><b-button
          v-if="!currentInvitation.fulfilled_at"
          variant="info"
          pill
          size="sm"
          @click="copyLink"
        >
          Copy Link
        </b-button>
        <b-button
          pill
          variant="danger"
          size="sm"
          :disabled="resending || revoking"
          @click="revokeInvitation"
        >
          <b-spinner v-if="revoking" small></b-spinner>
          <span v-else>Delete</span>
        </b-button>
      </div>
    </b-overlay>
  </div>
</template>

<script>
/* eslint-disable */
import { mapGetters } from "vuex";

export default {
  name: "AdminUserInvitationDetail",
  data() {
    return {
      isLoading: false,
      resending: false,
      revoking: false,
      currentInvitation: {},
    };
  },
  computed: {
    ...mapGetters("user", ["currentToken"]),
    ...mapGetters("agency", ["currentAgencyId", "currentAgency"]),
    inviteId() {
      return this.$route.params.inviteId;
    },
  },
  created() {
    this.fetchInvitation();
  },
  methods: {
    async fetchInvitation() {
      try {
        this.isLoading = true;
        const resp = await this.$ConveyApi.invitationsApi.getInvitation({ inviteId: this.inviteId });
        this.currentInvitation = {
          ...resp.data.data,
          roles: this.userRoles(resp.data.data.roles),
        };
      } catch(err) {
        console.log("[fetchInvitation] ERROR: ", err);
      } finally {
        this.isLoading = false;
      }
    },
    userRoles(roleIds) {
      if (roleIds) {
        return roleIds.split(",").map((roleId) => {
          const role = this.currentAgency.roles.find(
            (r) => r.role_id == roleId
          );
          return role ? role.role : "Unknown";
        });
      }
      return [];
    },
    async revokeInvitation() {
      try {
        const { invite_id } = this.currentInvitation;
        this.revoking = true;
        await this.$ConveyApi.invitationsApi.revokeInvitation({ inviteId: invite_id });
        this.$toastr("success", "The invitation has been revoked.");
      } catch (err) {
        console.log(err);
        this.$toastr(
          "error",
          "An error occurred when attempting to revoke the email invitation"
        );
      } finally {
        this.revoking = false;
        this.$router.push({ path: "/admin/invitations" }).catch(() => {});
      }
    },
    async resendInvitation() {
      try {
        const { invite_id } = this.currentInvitation;
        this.resending = true;
        await this.$ConveyApi.invitationsApi.resendInvitation({ inviteId: invite_id });
        this.$toastr("success", "Email invitation has been resent.");
      } catch (err) {
        console.log(err);
        this.$toastr(
          "error",
          "An error occurred when attempting to resend the email invitation"
        );
      } finally {
        this.resending = false;
        this.$router.push({ path: "/admin/invitations" }).catch(() => {});
      }
    },
    copyLink() {
      const { token } = this.currentInvitation;
      if (token) {
        const tokenUri = `${this.$basescheme}://${this.$baseuri}/t/${token}`;
        navigator.clipboard.writeText(tokenUri).then(() => {
          this.$toastr("success", "Link copied!");
        });
      } else {
        this.$toastr("error", "The invitation link doesn't exist!");
      }
    },
  },
};
</script>

<style lang="scss">
.admin-user-invitation-detail {
  max-width: 800px;
  width: 100%;
  height: auto;
  border-radius: 1rem;

  &--header {
    span.badge.admin {
      background-color: #f3ced6 !important;
      color: #ef5350 !important;
    }

    span.badge.operator {
      background-color: #b7f1e0 !important;
      color: #0acc95 !important;
    }
  }

  &--footer {
    gap: 0.5rem;
  }

  .custom-switch .custom-control-label::after {
    top: calc(0.25rem + 1px);
  }
}
</style>
